import { useEffect, useState } from 'react';
import { useRoute, useSession } from '@huaca/ui';
import { getFileDownloadUrl, getInstance, setInstanceLink } from '../api';
import { config } from '../config';
import { computeVisibility } from '@huaca/claro-engine';
import type { Answer, Field, Instance } from '../types';
import { Skeleton } from '../Skeleton';
import { StatusBadge } from './StatusBadge';

/**
 * The detail pane: who the client is, the shareable Room link (copy / revoke / renew), and the data
 * they've submitted so far — rendered from the instance's OWN snapshot, through the same conditions
 * engine, so the pro sees exactly the fields the client sees.
 */
export function InstanceDetail({ instanceId, onLinkChanged }: { instanceId: string; onLinkChanged?: () => void }) {
  const { client, can } = useSession();
  const { navigate } = useRoute();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setInstance(null);
    setError(null);
    getInstance(client, instanceId)
      .then((i) => live && setInstance(i))
      .catch(() => live && setError('לא ניתן לטעון את המפרט.'));
    return () => {
      live = false;
    };
  }, [client, instanceId]);

  if (error) return <p role="alert">{error}</p>;
  if (!instance) return <Skeleton title lines={5} />;

  const shareUrl = `${config.roomBase}/#i=${instance._id}`;
  const revoked = instance.link.revokedAt !== null;
  const expired = !revoked && instance.link.expiresAt !== null && new Date(instance.link.expiresAt).getTime() < Date.now();
  const linkLive = !revoked && !expired;

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (insecure context) — the link is selectable in the box */
    }
  }

  async function changeLink(action: 'revoke' | 'renew') {
    setBusy(true);
    try {
      const link = await setInstanceLink(client, instanceId, action);
      setInstance((i) => (i ? { ...i, link } : i));
      onLinkChanged?.();
    } finally {
      setBusy(false);
    }
  }

  const { visibleFieldIds, visibleSectionIds } = computeVisibility(instance.snapshot, instance.answers);

  // Orphans = answers whose field is no longer in the snapshot AT ALL — NOT merely hidden by a
  // condition (those stay in the snapshot, so we must use the full field-id set, not visibleFieldIds).
  // Their data is retained; we surface it in a collapsed group, labelled with the label the server
  // stamped at removal, so it never crowds the live form.
  const snapshotFieldIds = new Set(instance.snapshot.sections.flatMap((s) => s.fields.map((f) => f.id)));
  const orphans = Object.entries(instance.answers).filter(
    ([id, a]) => !snapshotFieldIds.has(id) && (a.file != null || (a.value !== null && a.value !== '')),
  );

  return (
    <div data-testid="instance-detail">
      <div className="claro-detail-head">
        <div className="claro-detail-toprow">
          <div className="claro-detail-title">
            <h2>{instance.client.fullName}</h2>
            <StatusBadge status={instance.status} />
          </div>
          {/* The client link lives as a small control cluster in the header corner, not its own
              section. Copy is icon-only (URL hidden to save space; it's on the title as a fallback
              and copy() writes it to the clipboard — needs a secure context). */}
          <div className="claro-detail-link">
            {can('claro:instance:editSchema') && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => navigate(`/instances/${instanceId}/snapshot`)}
                data-testid="edit-snapshot"
              >
                עריכת הטופס
              </button>
            )}
            {linkLive ? (
              <>
                <button
                  type="button"
                  className="claro-icon-btn claro-icon-btn--accent"
                  onClick={() => void copy()}
                  data-testid="copy-link"
                  title={
                    copied
                      ? 'הועתק'
                      : instance.link.expiresAt
                        ? `העתקת קישור · תוקף עד ${new Date(instance.link.expiresAt).toLocaleDateString('he-IL')}`
                        : 'העתקת קישור'
                  }
                  aria-label={copied ? 'הועתק' : 'העתקת קישור'}
                >
                  {copied ? <CheckIcon /> : <LinkIcon />}
                </button>
                <button type="button" className="btn-danger" disabled={busy} onClick={() => void changeLink('revoke')} data-testid="revoke-link">
                  ביטול
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => void changeLink('renew')}
                data-testid="renew-link"
                title={revoked ? 'הקישור בוטל' : 'תוקף הקישור פג'}
              >
                חידוש הקישור
              </button>
            )}
          </div>
        </div>
        <div className="claro-detail-contact">
          <span>
            <bdi>{instance.client.phone}</bdi>
          </span>
          <span>
            <bdi>{instance.client.emailRaw}</bdi>
          </span>
        </div>
      </div>

      <section>
        <h3>
          נתונים שהתקבלו{' '}
          <span className="claro-hint">
            {instance.completion.requiredOutstanding === 0
              ? '— כל שדות החובה מולאו'
              : `— ${instance.completion.requiredOutstanding} שדות חובה נותרו`}
          </span>
        </h3>
        {instance.snapshot.sections
          .filter((s) => visibleSectionIds.has(s.id))
          .map((section) => (
            <div key={section.id} className="claro-answer-section">
              <h4>{section.title}</h4>
              <dl className="claro-answers">
                {section.fields
                  .filter((f) => visibleFieldIds.has(f.id))
                  .map((field) => (
                    <div key={field.id} className="claro-answer-row">
                      <dt>{field.label}</dt>
                      <dd>
                        <AnswerValue field={field} instance={instance} />
                      </dd>
                    </div>
                  ))}
              </dl>
            </div>
          ))}

        {orphans.length > 0 && (
          <details className="claro-removed-fields" data-testid="removed-fields">
            <summary>שדות שהוסרו מהטופס · הנתונים נשמרו ({orphans.length})</summary>
            <dl className="claro-answers">
              {orphans.map(([id, answer]) => (
                <div key={id} className="claro-answer-row">
                  <dt>
                    <bdi>{answer.removedLabel ?? id}</bdi>
                  </dt>
                  <dd>
                    <OrphanValue instanceId={instance._id} fieldId={id} answer={answer} />
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </section>
    </div>
  );
}

function AnswerValue({ field, instance }: { field: Field; instance: Instance }) {
  const answer = instance.answers[field.id];
  if (field.type === 'file') {
    return answer?.file ? (
      <FileDownload instanceId={instance._id} fieldId={field.id} filename={answer.file.filename} />
    ) : (
      <span className="claro-empty">לא הועלה</span>
    );
  }
  const v = answer?.value;
  if (v === null || v === undefined || v === '') return <span className="claro-empty">—</span>;
  // <bdi>: client-entered text (a Latin name, an email typed into a text field, an id) can otherwise
  // reorder against the surrounding RTL punctuation.
  return (
    <span>
      <bdi>{String(v)}</bdi>
    </span>
  );
}

/**
 * Renders a RETAINED answer whose field was removed from the snapshot. There is no Field to key on
 * (the field is gone), so it branches on the answer shape: a file → the same download control; else the
 * scalar value, isolated with <bdi> because client-entered text can reorder against the RTL layout.
 */
function OrphanValue({ instanceId, fieldId, answer }: { instanceId: string; fieldId: string; answer: Answer }) {
  if (answer.file) return <FileDownload instanceId={instanceId} fieldId={fieldId} filename={answer.file.filename} />;
  const v = answer.value;
  if (v === null || v === undefined || v === '') return <span className="claro-empty">—</span>;
  return (
    <span>
      <bdi>{String(v)}</bdi>
    </span>
  );
}

/**
 * Download a client's uploaded file. The signed URL is minted on click (short-lived, never stored)
 * and opened in a new tab — the server sets a Content-Disposition filename, so the browser saves it
 * as the client's original name. A failure (file gone, link error) shows inline rather than throwing.
 */
function FileDownload({ instanceId, fieldId, filename }: { instanceId: string; fieldId: string; filename: string }) {
  const { client } = useSession();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const url = await getFileDownloadUrl(client, instanceId, fieldId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="claro-file-download" onClick={download} disabled={busy}>
      <bdi>{filename}</bdi>
      <span className="claro-file-download-hint">{busy ? '…' : failed ? 'שגיאה — נסו שוב' : 'הורדה'}</span>
    </button>
  );
}

/* Inline `currentColor` SVGs (the codebase has no shared icon set — Brand/ThemeToggle in @huaca/ui
   inline theirs the same way). aria-hidden: the accessible name is on the wrapping <button>. */

/** Chain-link — the "copy the client link" affordance. */
function LinkIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/** Check — the "copied" confirmation the link icon swaps to for 2s. */
function CheckIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}
